import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { arrayBufferToBase64 } from '../lib/crypto';
import { setConversationKeyRaw } from '../lib/conversation-crypto';
import { t } from '../lib/i18n';
import { connectDmRealtime } from '../lib/realtime/dm';
import { api } from '../lib/trpc/client';

type Conversation = { dmId: string; partnerUserId: string; partnerUsername: string; updatedAt: string };

export function DmPage() {
  const { circleId } = useParams<{ circleId: string }>();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [username, setUsername] = useState('');
  const [creating, setCreating] = useState(false);
  const [activeCircleName, setActiveCircleName] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [result, activeCircle] = await Promise.all([
        api.dm.getConversations(),
        api.circle.getActiveCircle()
      ]);
      setConversations(result.conversations);
      setActiveCircleName(activeCircle?.name ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('会話一覧の取得に失敗しました', 'Failed to load conversations'));
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshConversations = useCallback(async () => {
    try {
      const result = await api.dm.getConversations();
      setConversations(result.conversations);
    } catch {
      // Keep current list on background refresh failures.
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => connectDmRealtime(() => {
    void refreshConversations();
  }), [refreshConversations]);

  const createConversation = async () => {
    const partnerUsername = username.trim();
    if (!partnerUsername) {
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
      const raw = (await crypto.subtle.exportKey('raw', key)) as ArrayBuffer;
      const rawBase64 = arrayBufferToBase64(raw);

      const created = await api.dm.createConversation({
        partnerUsername,
        fingerprintVerified: false,
        wrappedKeyForSelf: rawBase64,
        wrappedKeyForPartner: rawBase64
      });

      setConversationKeyRaw('dm', created.dmId, rawBase64);
      setUsername('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('会話の作成に失敗しました', 'Failed to create conversation'));
    } finally {
      setCreating(false);
    }
  };

  return (
    <main className="h-[calc(100vh-4rem)] overflow-hidden rounded-xl border border-border bg-card/60">
      <header className="space-y-2">
        <h1 className="px-4 pt-4 text-2xl font-semibold">{t('DM', 'DM')}</h1>
        <p className="px-4 text-sm text-muted-foreground">{t('Discord風レイアウトで1対1会話を管理します。', 'Manage one-to-one conversations in a Discord-style layout.')}</p>
        <p className="px-4 text-xs text-muted-foreground">{t('サークル', 'Circle')}: {activeCircleName || t('未選択', 'Not selected')}</p>
      </header>

      {error ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <div className="grid h-[calc(100%-4.5rem)] grid-cols-1 md:grid-cols-[320px_1fr]">
        <aside className="border-r border-border p-3">
          <Card>
            <CardHeader>
              <CardTitle>{t('新しいDM', 'New DM')}</CardTitle>
              <CardDescription>{t('相手のユーザー名を入力してください。', "Enter your partner's username.")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder={t('相手のユーザー名', 'Partner username')} />
              <Button onClick={createConversation} disabled={creating || !username.trim()} className="w-full">
                {creating ? t('作成中...', 'Creating...') : t('作成', 'Create')}
              </Button>
            </CardContent>
          </Card>

          <div className="mt-3 space-y-2">
            {loading ? (
              <>
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-full" />
              </>
            ) : conversations.length === 0 ? (
              <p className="px-2 text-sm text-muted-foreground">{t('会話はまだありません。', 'No conversations yet.')}</p>
            ) : (
              conversations.map((conversation) => (
                <Link
                  key={conversation.dmId}
                  to={`/${circleId}/dm/${conversation.dmId}`}
                  className="block rounded-md border border-border px-3 py-2 text-sm hover:bg-muted"
                >
                  <p className="font-medium">{conversation.partnerUsername}</p>
                  <p className="text-xs text-muted-foreground">{new Date(conversation.updatedAt).toLocaleString()}</p>
                </Link>
              ))
            )}
          </div>
        </aside>

        <section className="flex items-center justify-center p-6">
          <div className="text-center">
            <h2 className="text-xl font-semibold">{t('会話を選択してください', 'Select a conversation')}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{t('左の一覧からDMを選ぶとチャットを表示します。', 'Choose a DM from the left list to open chat.')}</p>
          </div>
        </section>
      </div>
    </main>
  );
}
